import { H3, ModuleHeader, Tooltip } from '@/components'
import { Card, Input, Select } from '@/components_deprecated'
import { format, useDebounce } from '@/utils'
import { useQuery } from '@apollo/client'
import { Loader } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { addWeeks, isSameDay, parse, subDays } from 'date-fns'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled, { useTheme } from 'styled-components'
import { VEHICLE_STATUS_QUERY } from '../queries'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
  position: relative;
`

const Controls = styled(Card)`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 1rem;
`

const GraphWrapper = styled(Card)``

const GraphRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  flex-direction: row;
`

const WeekWrapper = styled.div`
  display: flex;
  flex-direction: row;

  border: 1px solid ${props => props.theme.colors.white};
`

const Labels = styled.div`
  display: flex;
  flex-direction: row;
  border: 1px solid ${props => props.theme.colors.white};
`

const Label = styled.div<{ isMobile: boolean }>`
  width: ${props => (props.isMobile ? '84px' : '43.3px')};
  min-width: ${props => (props.isMobile ? '84px' : '43.3px')};
  height: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
`

const DayBox = styled.div<{
  isComplete: boolean
  isFile: boolean
  isFuture: boolean
  isMobile: boolean
  isSameDay: boolean
}>`
  width: ${props => (props.isMobile ? '12px' : '6px')};
  height: 20px;
  margin: 0;
  background-color: ${props =>
    props.isSameDay
      ? props.theme.colors.gray6
      : props.isFuture
      ? props.theme.colors.gray8
      : props.isComplete
      ? props.isFile
        ? props.theme.colors.blue400
        : props.theme.colors.green600
      : props.theme.colors.red};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  &:hover {
    border: 1px solid ${props => props.theme.colors.white};
  }
`

const ColorDot = styled.span<{ color: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.color};
  margin-right: 2px;
`

const ColorWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  margin: 0.5rem 1rem;
  background-color: ${props => props.theme.colors.background};

  > span {
    margin-right: 1rem;
  }
`

interface Props {
  tabs?: ReactNode
}

export const VehicleActivitiesStatus: React.FC<Props> = ({ tabs }) => {
  const translations = useTranslate({
    activitiesStatus: 'activities.activities-status',
    weekN: ['common.week-n', { n: '0' }],
    search: 'common.search',
    APIAndDDD: 'common.api-and-ddd',
    none: 'common.none',
  })
  const navigate = useNavigate()
  const theme = useTheme()

  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('BOTH')

  const debouncedSearch = useDebounce(search, 300)

  const today = new Date()

  const dateEnd = subDays(addWeeks(today, 1), today.getDay())

  const { data, loading } = useQuery(VEHICLE_STATUS_QUERY, {
    variables: {
      q: debouncedSearch,
      dateStart: format(addWeeks(dateEnd, isMobile ? -3 : -26), 'yyyy-MM-dd'),
      dateEnd: format(dateEnd, 'yyyy-MM-dd'),
      mode: filter,
    },
  })

  const statuses = data?.allVehicleStatuses || []

  return (
    <Wrapper>
      <ModuleHeader
        className="flex-col items-start gap-2"
        contentClassName="items-center justify-between gap-4 w-full"
        title={translations.activitiesStatus}
      />
      {tabs}
      <Controls>
        <Input
          placeholder={translations.search}
          value={search}
          onChange={val => setSearch(val)}
          grayed
          fullWidth={isMobile}
        />
        <Select
          value={filter}
          onChange={option => setFilter(option ?? 'BOTH')}
          grayed
          options={[
            { label: translations.APIAndDDD, value: 'BOTH' },
            { label: 'API', value: 'API' },
            { label: 'DDD', value: 'DDD' },
          ]}
          fullWidth={isMobile}
        />
      </Controls>
      {loading && <Loader.Dots />}
      {!loading && (
        <ColorWrapper>
          <span>
            <ColorDot color={theme.colors.green600} />
            {' API'}
          </span>
          <span>
            <ColorDot color={theme.colors.blue400} />
            {' DDD'}
          </span>
          <span>
            <ColorDot color={theme.colors.red} />
            {' ' + translations.none}
          </span>
        </ColorWrapper>
      )}
      {!loading && (
        <GraphWrapper $overflowX="scroll">
          {statuses.map(status => (
            <div key={status.vehicle.id}>
              <H3>{status.vehicle.registrationNumber}</H3>
              <Labels>
                {status.weekStatuses.map(weekStatus => (
                  <Label
                    key={'label ' + weekStatus.weekNumber}
                    isMobile={isMobile}
                  >
                    {isMobile
                      ? translations.weekN({
                          n: weekStatus.weekNumber,
                        })
                      : weekStatus.weekNumber}
                  </Label>
                ))}
              </Labels>
              <GraphRow>
                {status.weekStatuses.map(weekStatus => (
                  <WeekWrapper key={weekStatus.weekNumber}>
                    {weekStatus.dayStatuses.map(dayStatus => (
                      <Tooltip
                        key={dayStatus.date}
                        content={format(
                          parse(dayStatus.date, 'yyyy-MM-dd', new Date()),
                          'dd. MMM'
                        )}
                      >
                        <DayBox
                          isComplete={dayStatus.isComplete}
                          isFile={dayStatus.source === 'DDD'}
                          isFuture={
                            parse(dayStatus.date, 'yyyy-MM-dd', new Date()) >
                            today
                          }
                          isSameDay={isSameDay(
                            parse(dayStatus.date, 'yyyy-MM-dd', new Date()),
                            today
                          )}
                          onClick={() =>
                            navigate(`/vehicle-fleet/${status.vehicle.id}`)
                          }
                          isMobile={isMobile}
                        />
                      </Tooltip>
                    ))}
                  </WeekWrapper>
                ))}
              </GraphRow>
            </div>
          ))}
        </GraphWrapper>
      )}
    </Wrapper>
  )
}
